import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";
import StampQuote from "@/components/StampQuote";
import useSEO from "@/lib/useSEO";

const FILTERS = [
  { key: null, label: "All of it" },
  { key: "tee", label: "Unisex Heavyweight" },
  { key: "womens-tee", label: "Women's Relaxed" },
  { key: "hoodie", label: "Hoodies" },
  { key: "baby-tee", label: "Fitted Baby Tee" },
];

export default function Shop() {
  const SHOP_SEO = {
    all: [
      "Shop All Heavyweight Tees & Hoodies | Fleece The Regime",
      "Browse every design: unisex heavyweight tees and women's relaxed tees at $29.99, fitted baby tees at $34.99, heavyweight hoodies at $59.99. Printed to order in the USA.",
    ],
    tee: [
      "Heavyweight Protest Tees | Fleece The Regime",
      "Shop heavy 6.1 oz garment-dyed cotton tees with funny, sharp protest slogans. Black, white and grey. Sizes S-4XL. Printed to order in the USA.",
    ],
    hoodie: [
      "Heavyweight Protest Hoodies | Fleece The Regime",
      "Shop 340 gsm heavyweight fleece hoodies with bold satirical slogans. Black, grey and white colorways, sizes S-3XL. Printed to order, ships tracked.",
    ],
    "baby-tee": [
      "Fitted Baby Tees With Simple Slogans | Fleece The Regime",
      "Shop fitted micro-rib baby tees with short, sharp protest slogans. White, heather grey and black, sizes XS-2XL. Printed to order in the USA.",
    ],
    "womens-tee": [
      "Women's Relaxed Protest Tees | Fleece The Regime",
      "Shop women's-cut relaxed tees with sharp protest slogans — finally cut for women. Black, white and heather grey, sizes XS-2XL. Printed to order in the USA.",
    ],
  };
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const type = params.get("type");
  useSEO(...(SHOP_SEO[type || "all"] || SHOP_SEO.all), false, "/shop/");

  const artwork = products.filter((p) => p.style !== "minimal");
  const minimal = products.filter((p) => p.style === "minimal");
  const mixed = [];
  let ai = 0;
  let mi = 0;
  while (ai < artwork.length || mi < minimal.length) {
    if (ai < artwork.length) mixed.push(artwork[ai++]);
    if (mi < minimal.length) mixed.push(minimal[mi++]);
  }

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: type ? { type } : {} })
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [type]);

  return (
    <main data-testid="shop-page" className="pt-16">
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-14 border-b-2 border-black" data-testid="shop-header-band">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">The Catalog</p>
        <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tighter" data-testid="shop-heading">
          Wearable<br />Dissent.
        </h1>
        <p className="mt-8 max-w-xl font-mono text-sm leading-relaxed text-black/60" data-testid="shop-intro">
          {type === "tee"
            ? "Our tees are printed on genuine Comfort Colors 1717 blanks: 6.1 oz of garment-dyed, ring-spun heavyweight cotton with a relaxed, lived-in fit that gets better every wash. Every design is print-shop satire — sharp enough to quote, soft enough to sleep in. Sizes S to 4XL, in black, white and grey. No thin fabric. No thin arguments."
            : type === "hoodie"
              ? "Our hoodies are built on heavyweight 340 gsm three-end fleece — the Lane Seven LS19001 in black, grey and white: 70% cotton, 30% recycled polyester, with a three-panel fleece-lined hood and kangaroo pocket. Sizes S to 3XL. Warm enough for a canvassing shift, loud enough for the comments section."
              : type === "baby-tee"
                ? "Our baby tees are the Bella+Canvas 1010: a fitted, mid-length micro-rib tee in a soft 52% combed cotton / 48% polyester blend, in white, heather grey and black, sizes XS to 2XL. Short slogans, hard principles — printed to order like everything else here."
                : type === "womens-tee"
                  ? "Our women's tees are the Next Level NL3910 boyfriend cut: 4.3 oz combed ring-spun cotton, relaxed and side-seamed — cut for women, not shrunk from a men's blank. Black, white and heather grey, sizes XS to 2XL. The same slogans, finally in your fit."
                  : "Every piece here is made-to-order, printed satire: unisex heavyweight tees on 6.1 oz garment-dyed cotton, women's relaxed tees on 4.3 oz combed ring-spun Next Level cotton, fitted baby tees on 5.3 oz micro-rib, and hoodies on 340 gsm heavyweight fleece. Nothing ships until you order it — no warehouse, no waste, no weak fabric. Pick a slogan, pick a color, wear it somewhere it matters."}
        </p>
      </section>

      <div className="flex flex-wrap border-b-2 border-black font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.15em]">
        {FILTERS.map((f) => (
          <button
            key={f.label}
            data-testid={`filter-${f.key || "all"}`}
            onClick={() => setParams(f.key ? { type: f.key } : {})}
            className={`px-6 py-4 border-r-2 border-black transition-colors ${
              (type || null) === f.key ? "bg-black text-background" : "hover:bg-accent"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto hidden sm:flex items-center px-6 text-muted-foreground" data-testid="shop-count">
          {products.length} pieces of dissent
        </span>
      </div>

      <section className="px-6 sm:px-12 lg:px-20 py-20">
        {loading ? (
          <p className="font-mono text-sm text-muted-foreground" data-testid="shop-loading">Loading the resistance…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-12">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      <StampQuote kicker="HOUSE RULE" text="No human is illegal." />

      <Marquee
        items={["First item ships free on orders $65+", "Honest specs only", "Printed with spite & care", "Card payments via Stripe"]}
        className="bg-black text-background"
      />
    </main>
  );
}
